<template>
  <main class="bg-[#C6202B]">
    <!-- Hero Carousel Section -->
    <section 
      class="sticky top-0 h-[70vh] sm:h-[75vh] lg:h-screen overflow-hidden select-none z-0"
      :class="isDragging ? 'cursor-grabbing' : 'cursor-default'"
      @mousedown="handleDragStart"
      @mousemove="handleDragMove"
      @mouseup="handleDragEnd"
      @mouseleave="handleDragEnd"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleDragEnd"
    >
      <!-- Carousel Background -->
      <div class="absolute inset-0 z-0 pointer-events-none">
        <div 
          class="relative h-full w-full flex"
          :class="{ 'transition-transform duration-700 ease-out': !isDragging && !isJumping }"
          :style="{ transform: `translateX(calc(-${currentIndex * 100}% + ${currentTranslate}px))` }"
          @transitionend="handleTransitionEnd"
        >
          <div 
            v-for="(banner, index) in displayBanners" 
            :key="`${banner.url}-${index}`"
            class="relative h-full w-full flex-shrink-0 bg-cover bg-center"
            :style="{ backgroundImage: `url(${banner.url})` }"
          >
            <!-- Overlay for text readability -->
            <div class="absolute inset-0 bg-black/40"></div>
          </div>
        </div>
      </div>

      <!-- Hero Content -->
      <div class="container-custom relative z-10 h-full flex flex-col pointer-events-none">
        <div class="flex-1 flex items-center mt-16 sm:mt-20 relative">
          <div class="max-w-6xl w-full overflow-hidden pointer-events-auto">
            <transition name="slide-up" mode="out-in">
              <div 
                v-if="!isDragging && !isSettling"
                :key="currentBannerIndex" 
                class="space-y-4 sm:space-y-6 lg:space-y-10 text-left py-4 pb-20 sm:pb-4"
              >
                <h1 :class="[$i18n.locale === 'en' ? 'text-4xl md:text-[4rem] lg:text-[5.25rem]' : 'text-4xl md:text-7xl lg:text-8xl', 'font-bold text-white leading-[1.1]']">
                  <div class="mb-2 lg:mb-4">{{ banners[currentBannerIndex].title1 }}</div>
                  <span class="bg-gradient-button bg-clip-text text-transparent">{{ banners[currentBannerIndex].title2 }}</span>
                </h1>
                <h2 :class="[$i18n.locale === 'en' ? 'text-2xl md:text-[2.75rem] lg:text-[3.5rem]' : 'text-2xl md:text-5xl lg:text-6xl', 'text-white font-medium']">
                  {{ banners[currentBannerIndex].subtitle }}
                </h2>
                <p :class="[$i18n.locale === 'en' ? 'text-base sm:text-[1.15rem]' : 'text-base sm:text-xl', 'text-white/90 font-light max-w-3xl leading-relaxed']">
                  {{ banners[currentBannerIndex].desc }}
                </p>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Carousel Indicators -->
      <div class="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-3 pointer-events-auto">
        <button 
          v-for="(_, index) in banners" 
          :key="index"
          @click="goToBanner(index)"
          class="w-12 h-1.5 rounded-full transition-all duration-300 cursor-pointer"
          :class="currentBannerIndex === index ? 'bg-[#C6202B] w-16' : 'bg-white/30 hover:bg-white/50'"
        ></button>
      </div>
    </section>

    <!-- Content Sections Wrapper (Covers the sticky Hero on scroll) -->
    <div class="relative z-10 bg-gradient-to-b from-[#CB2530] to-[#B41923]">
    <section class="py-20 relative overflow-hidden">
      <!-- Background Decoration Curves -->
      <div class="absolute inset-0 -z-10">
        <div class="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-white/10 rounded-full blur-[100px] opacity-50"></div>
        <div class="absolute bottom-1/3 left-1/3 w-[500px] h-[500px] bg-[#8E1219]/25 rounded-full blur-[100px] opacity-50"></div>
        <svg class="absolute top-0 left-0 w-full h-full opacity-20" viewBox="0 0 1440 800" preserveAspectRatio="none">
          <path d="M-100 600C200 400 600 800 1000 400C1400 0 1600 200 1800 100" stroke="url(#lineGradient)" stroke-width="2.5" fill="none" />
          <path d="M-50 700C300 500 700 900 1100 500C1500 100 1700 300 1900 200" stroke="url(#lineGradient)" stroke-width="1.5" fill="none" opacity="0.4" />
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="transparent" />
              <stop offset="50%" stop-color="#FFFFFF" />
              <stop offset="100%" stop-color="transparent" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="text-center mb-16">
          <h2 class="text-4xl md:text-5xl text-white mb-4 tracking-tight">{{ $t('common.home.coreAdvantages') }}</h2>
          <p class="text-xl text-white/80 font-light">
            {{ $t('common.home.strictProcess') }}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{{ $t('common.home.excellentQuality') }}
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div 
            v-for="adv in advantages" 
            :key="adv.advantageId"
            class="group p-8 bg-white/10 backdrop-blur-sm border border-white/25 rounded-2xl hover:bg-white/15 hover:border-white/60 transition-all duration-500"
          >
            <div class="w-12 h-12 mb-4 text-white transition-transform duration-200 group-hover:scale-110 flex items-center justify-center">
              <img v-if="isExternalUrl(adv.iconName)" :src="processImageUrl(adv.iconName)" class="w-full h-full object-contain" />
              <i v-else :class="getIconClass(adv.iconName)" class="text-3xl"></i>
            </div>
            <h3 class="text-2xl text-white mb-3 tracking-tight">{{ adv.title }}</h3>
            <p class="text-white/80 leading-relaxed">{{ adv.description }}</p>
          </div>

          <!-- Fallback if no advantages from API -->
          <template v-if="advantages.length === 0">
            <div class="group p-8 bg-white/10 backdrop-blur-sm border border-white/25 rounded-2xl hover:bg-white/15 hover:border-white/60 transition-all duration-500">
              <div class="w-12 h-12 mb-4 text-white transition-transform duration-200 group-hover:scale-110">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="text-2xl text-white mb-3 tracking-tight">精密工艺</h3>
              <p class="text-white/80 leading-relaxed">线径精度 ±0.5μm，微米级控制标准</p>
            </div>
            <div class="group p-8 bg-white/10 backdrop-blur-sm border border-white/25 rounded-2xl hover:bg-white/15 hover:border-white/60 transition-all duration-500">
              <div class="w-12 h-12 mb-4 text-white transition-transform duration-200 group-hover:scale-110">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="text-2xl text-white mb-3 tracking-tight">全球认证</h3>
              <p class="text-white/80 leading-relaxed">通过 RoHS 2.0, REACH, UL 国际认证</p>
            </div>
            <div class="group p-8 bg-white/10 backdrop-blur-sm border border-white/25 rounded-2xl hover:bg-white/15 hover:border-white/60 transition-all duration-500">
              <div class="w-12 h-12 mb-4 text-white transition-transform duration-200 group-hover:scale-110">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full">
                  <circle cx="12" cy="8" r="7"/><path d="M3.3 19a9 9 0 0 1 17.4 0" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="text-2xl text-white mb-3 tracking-tight">专业团队</h3>
              <p class="text-white/80 leading-relaxed">行业资深技术生产团队，20+年一线制造经验</p>
            </div>
          </template>
        </div>
      </div>
    </section>

    <!-- Description Section -->
    <section class="py-20 relative overflow-hidden">
      <!-- Background Decoration Curves -->
      <div class="absolute inset-0 -z-10">
        <div class="absolute inset-0 opacity-30 pointer-events-none">
          <svg width="100%" height="100%" viewBox="0 0 1440 800" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1560 100C1300 400 940 340 640 600C400 810 280 1100 0 800" stroke="url(#vision_curve_1)" stroke-width="3" />
            <path d="M1520 340C1260 100 980 60 740 170C460 300 260 560 -40 300" stroke="url(#vision_curve_2)" stroke-width="2" />
            <defs>
              <linearGradient id="vision_curve_1" x1="1560" y1="100" x2="0" y2="800" gradientUnits="userSpaceOnUse">
                <stop offset="0" stop-color="#FFFFFF" stop-opacity="0.15" />
                <stop offset="0.5" stop-color="#FFFFFF" stop-opacity="0.45" />
                <stop offset="1" stop-color="#FFFFFF" stop-opacity="0.1" />
              </linearGradient>
              <linearGradient id="vision_curve_2" x1="1520" y1="340" x2="-40" y2="300" gradientUnits="userSpaceOnUse">
                <stop offset="0" stop-color="#FFFFFF" stop-opacity="0.1" />
                <stop offset="0.5" stop-color="#FFFFFF" stop-opacity="0.3" />
                <stop offset="1" stop-color="#FFFFFF" stop-opacity="0.08" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16 relative z-10">
        <h2 class="text-4xl text-white mb-6 tracking-tight">{{ configs.vision_title }}</h2>
        <div 
          :class="[$i18n.locale === 'en' ? 'text-left hyphens-auto' : 'text-justify', 'text-lg text-white/80 leading-relaxed break-words space-y-4']"
          :lang="$i18n.locale"
        >
          <p v-for="(paragraph, index) in configs.vision_desc.split('\n')" :key="index" v-if="paragraph.trim()">
            {{ paragraph }}
          </p>
        </div>
      </div>
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div class="relative">
          <div class="aspect-video rounded-2xl overflow-hidden border border-white/25 bg-black/40 w-full relative group">
            <!-- 替换图片为可交互视频 -->
            <video 
              v-if="isVideo(configs.vision_video || configs.vision_image)"
              :src="configs.vision_video || configs.vision_image" 
              class="w-full h-full object-contain"
              autoplay 
              muted 
              loop 
              controls
              playsinline
            ></video>
            <img 
              v-else
              :src="configs.vision_image" 
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" 
              alt="工厂展示"
            >
          </div>
          <div class="absolute -bottom-6 -right-6 w-48 h-48 bg-[#8E1219]/30 rounded-2xl blur-2xl -z-10"></div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-32 relative overflow-hidden">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <h2 class="text-4xl md:text-5xl text-white mb-6 tracking-tight">{{ configs.cta_title }}</h2>
        <div 
          :class="[$i18n.locale === 'en' ? 'text-left hyphens-auto' : 'text-center', 'text-xl text-white/80 mb-12 max-w-3xl mx-auto break-words space-y-4']"
          :lang="$i18n.locale"
        >
          <p v-for="(paragraph, index) in configs.cta_desc.split('\n')" :key="index" v-if="paragraph.trim()">
            {{ paragraph }}
          </p>
        </div>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            @click="handleContactJump"
            class="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-white hover:bg-[#FDF3F4] text-[#B41923] text-lg font-medium rounded-lg transition-all shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/20"
          >
            {{ configs.cta_btn_text }}
          </button>
        </div>
      </div>
      <div class="absolute inset-0 bg-[#8E1219]/25 blur-[120px] rounded-full max-w-2xl mx-auto pointer-events-none"></div>
    </section>
    </div>
  </main>
</template>

<script>
import { getHomeBanners, getHomeAdvantages, getHomeConfigs } from '@/api/home'

export default {
  name: 'HomeView',
  data() {
    return {
      currentIndex: 1, // 初始指向克隆后的第一个真实 banner
      banners: [
        {
          url: require('../assets/images/banner/banner1.jpg'),
          title1: '匠心智造',
          title2: '微细漆包线',
          subtitle: '精密制造专家',
          desc: '专注 0.035 mm - 1.200 mm 漆包铜圆线制造'
        },
        {
          url: require('../assets/images/banner/banner2.jpg'),
          title1: '品质先行',
          title2: '卓越性能',
          subtitle: '严苛工艺标准',
          desc: '通过国际权威认证，确保每一米线材的高可靠性'
        },
        {
          url: require('../assets/images/banner/banner3.jpg'),
          title1: '创新驱动',
          title2: '智领未来',
          subtitle: '深耕行业20载',
          desc: '持续研发投入，助力全球电子制造产业升级'
        }
      ],
      advantages: [
        { advantageId: 1, title: '精密工艺', description: '线径精度 ±0.5μm，微米级控制标准', iconName: 'Zap' },
        { advantageId: 2, title: '全球认证', description: '通过 RoHS 2.0, REACH, UL 国际认证', iconName: 'Shield' },
        { advantageId: 3, title: '专业团队', description: '行业资深技术生产团队，20+年一线制造经验', iconName: 'Users' }
      ],
      configs: {
        vision_title: '一丝不苟，创新通达',
        vision_desc: '星虹达坚信，高端制造的基石在于原材料的可靠性。我们不以牺牲性能为代价追求规模，而是通过持续优化拉丝退火工艺与绝缘涂层技术，确保每一米线材的导电性、耐热性与附着性等各项性能达到极致平衡。',
        vision_image: require('../assets/images/factory-view.png'),
        vision_video: '',
        cta_title: '携手共进，合作双赢',
        cta_desc: '期待与您探讨定制化解决方案，共同推动漆包线应用技术的边界，实现互利共赢的长远发展。',
        cta_btn_text: '立即咨询'
      },
      timer: null,
      startX: 0,
      isDragging: false,
      isSettling: false,
      isJumping: false,
      currentTranslate: 0
    }
  },
  computed: {
    // 构造 [末尾克隆, 1, 2, 3, 开头克隆] 的数组
    displayBanners() {
      if (this.banners.length === 0) return []
      return [
        this.banners[this.banners.length - 1],
        ...this.banners,
        this.banners[0]
      ]
    },
    // 当前显示的真实 banner 索引 (用于文字和指示器)
    currentBannerIndex() {
      if (this.currentIndex === 0) return this.banners.length - 1
      if (this.currentIndex === this.displayBanners.length - 1) return 0
      return this.currentIndex - 1
    }
  },
  created() {
    this.fetchHomeData()
    this.$root.$on('language-changed', this.fetchHomeData)
  },
  mounted() {
    this.startTimer()
  },
  activated() {
    this.startTimer()
  },
  deactivated() {
    this.stopTimer()
  },
  beforeDestroy() {
    this.stopTimer()
    this.$root.$off('language-changed', this.fetchHomeData)
  },
  methods: {
    async fetchHomeData() {
      try {
        // 1. 获取轮播图
        const bannerRes = await getHomeBanners()
        if (bannerRes.data && bannerRes.data.length > 0) {
          this.banners = bannerRes.data.map(item => ({
            url: this.processImageUrl(item.bannerUrl),
            title1: item.titleMain,
            title2: item.titleGradient,
            subtitle: item.subtitle,
            desc: item.description
          }))
        }

        // 2. 获取核心优势
        const advantageRes = await getHomeAdvantages()
        if (advantageRes.data && advantageRes.data.length > 0) {
          this.advantages = advantageRes.data
        }

        // 3. 获取板块配置
        const configRes = await getHomeConfigs()
        if (configRes.data && configRes.data.length > 0) {
          configRes.data.forEach(item => {
            if (this.configs.hasOwnProperty(item.configKey)) {
              if ((item.configKey === 'vision_image' || item.configKey === 'vision_video') && item.configValue) {
                this.configs[item.configKey] = this.processImageUrl(item.configValue)
              } else {
                this.configs[item.configKey] = item.configValue
              }
            }
          })
        }
      } catch (error) {
        console.error('Failed to fetch home data:', error)
      }
    },
    processImageUrl(url) {
      if (!url) return ''
      if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
        return url
      }
      const baseUrl = process.env.VUE_APP_BASE_API || 'http://localhost:8080'
      return baseUrl + url
    },
    isVideo(url) {
      if (!url) return false
      const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov']
      return videoExtensions.some(ext => url.toLowerCase().endsWith(ext))
    },
    isExternalUrl(url) {
      return url && (url.startsWith('/') || url.startsWith('http'))
    },
    getIconClass(name) {
      const iconMap = {
        Zap: 'el-icon-bangzhu',
        Shield: 'el-icon-s-check',
        Users: 'el-icon-user-solid',
        Cpu: 'el-icon-cpu',
        Target: 'el-icon-bangzhu',
        TrendingUp: 'el-icon-data-line',
        Leaf: 'el-icon-odometer',
        Handshake: 'el-icon-s-check'
      }
      return iconMap[name] || 'el-icon-star-on'
    },
    startTimer() {
      this.stopTimer() // 确保先清除旧定时器，防止叠加
      this.timer = setInterval(() => {
        this.nextBanner()
      }, 5000)
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
      }
    },
    nextBanner() {
      if (this.isJumping) return
      this.currentIndex++
    },
    prevBanner() {
      if (this.isJumping) return
      this.currentIndex--
    },
    goToBanner(index) {
      this.currentIndex = index + 1
    },
    handleTransitionEnd() {
      // 当滚动到末尾克隆（显示的是图1）时，瞬间跳转回真实的图1
      if (this.currentIndex >= this.displayBanners.length - 1) {
        this.isJumping = true
        this.currentIndex = 1
        this.$nextTick(() => {
          setTimeout(() => { this.isJumping = false }, 50)
        })
      }
      // 当滚动到开头克隆（显示的是最后一张图）时，瞬间跳转回真实的最后一张图
      else if (this.currentIndex <= 0) {
        this.isJumping = true
        this.currentIndex = this.displayBanners.length - 2
        this.$nextTick(() => {
          setTimeout(() => { this.isJumping = false }, 50)
        })
      }
      
      // 文字动画结算逻辑
      if (this.isSettling) {
        this.isSettling = false
      }
    },
    handleDragStart(e) {
      if (this.isJumping) return
      this.isDragging = true
      this.startX = e.pageX
      this.stopTimer()
    },
    handleTouchStart(e) {
      if (this.isJumping) return
      this.isDragging = true
      this.startX = e.touches[0].pageX
      this.stopTimer()
    },
    handleDragMove(e) {
      if (!this.isDragging) return
      const currentX = e.pageX
      this.currentTranslate = currentX - this.startX
    },
    handleTouchMove(e) {
      if (!this.isDragging) return
      const currentX = e.touches[0].pageX
      this.currentTranslate = currentX - this.startX
    },
    handleDragEnd() {
      if (!this.isDragging) return
      this.isDragging = false
      
      const threshold = 50 // 触发切换的阈值
      if (this.currentTranslate < -threshold) {
        this.isSettling = true
        this.nextBanner()
      } else if (this.currentTranslate > threshold) {
        this.isSettling = true
        this.prevBanner()
      } else {
        // 未达到阈值，回弹
        this.currentIndex = this.currentIndex
      }
      
      this.currentTranslate = 0
      this.startTimer()
    },
    handleContactJump() {
      this.$root.$emit('navigate', 'contact')
    }
  }
}
</script>

<style scoped>
/* Slide Up and Fade In animation for Carousel Text */
.slide-up-enter-active {
  transition: all 1.8s cubic-bezier(0.16, 1, 0.3, 1); /* 恢复缓慢大气的速度 */
}
.slide-up-leave-active {
  transition: opacity 0.2s ease-out;
  /* 移除 position: absolute，防止在 Flex 布局中脱离文档流导致位置跳动 */
}
.slide-up-enter {
  opacity: 0;
  transform: translateY(60px);
}
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(0); /* 显式强制不发生位移 */
}
</style>
